import { Controller } from "@hotwired/stimulus"

// Kosten form: after uploads_controller has uploaded the invoice (uploads:fileUploaded), posts
// its signed id to /asset_costs/extract and fills the answer into the form. A field is only
// filled while it still holds what it had when the drawer opened — the default date and
// category count as untouched, anything the user typed or picked does not.
//
// Brutto is written before Netto and announced with an input event, so asset-cost-amounts
// treats it as entered and does not overwrite it with its own Netto × USt pre-fill.
const FIELDS = [
  "incurred_on",
  "costable_key",
  "asset_cost_category_id",
  "description",
  "vendor_name",
  "gross_amount",
  "net_amount",
  "odometer_km",
  "note",
]

export default class extends Controller {
  static targets = ["indicator", "fields", "notice"]
  static values = { url: String }

  connect() {
    this.initial = Object.fromEntries(FIELDS.map((name) => [name, this.field(name)?.value ?? ""]))
    this.onFileUploaded = this.onFileUploaded.bind(this)
    this.element.addEventListener("uploads:fileUploaded", this.onFileUploaded)
  }

  disconnect() {
    this.element.removeEventListener("uploads:fileUploaded", this.onFileUploaded)
  }

  async onFileUploaded(event) {
    const signedId = event.detail?.signedId
    if (!signedId) return

    this.busy(true)
    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
        },
        credentials: "same-origin",
        body: JSON.stringify({ signed_id: signedId }),
      })
      const payload = response.ok ? await response.json() : { suggestions: {} }
      const filled = this.apply(payload.suggestions || {})
      this.announce(filled)
    } catch {
      this.announce(0)
    } finally {
      this.busy(false)
    }
  }

  // Returns how many fields were filled.
  apply(suggestions) {
    let filled = 0
    FIELDS.forEach((name) => {
      const value = suggestions[name]
      const input = this.field(name)
      if (value === undefined || value === null || value === "" || !input) return
      if (input.type === "hidden" && name === "costable_key") return // the asset was fixed by the tab
      if (input.value !== this.initial[name]) return // the user changed it

      this.write(input, String(value))
      filled += 1
    })
    return filled
  }

  write(input, value) {
    const slim = this.application.getControllerForElementAndIdentifier(input, "select")?.slimselect
    if (slim) {
      slim.setSelected(value)
    } else {
      input.value = value
    }
    input.dispatchEvent(new Event("input", { bubbles: true }))
    input.dispatchEvent(new Event("change", { bubbles: true }))
  }

  field(name) {
    return this.element.querySelector(`[name="asset_cost[${name}]"]`)
  }

  busy(state) {
    if (this.hasIndicatorTarget) this.indicatorTarget.classList.toggle("hidden", !state)
    if (this.hasFieldsTarget) this.fieldsTarget.classList.toggle("hidden", state)
  }

  announce(filled) {
    if (!this.hasNoticeTarget) return

    const key = filled > 0 ? "filledText" : "emptyText"
    this.noticeTarget.textContent = this.noticeTarget.dataset[key] || ""
    this.noticeTarget.classList.toggle("hidden", this.noticeTarget.textContent === "")
  }
}
